// View_Calculated_Month_End_Payroll.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const getInitials = (fullName = "") => {
  const tokens = String(fullName)
    .replace(/[^\p{L}\p{N}\s'-]/gu, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return "??";
  if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
  return (tokens[0][0] + tokens[tokens.length - 1][0]).toUpperCase();
};

const avatarBgClass = (seed = "") => {
  const palette = [
    "bg-sky-500",
    "bg-indigo-500",
    "bg-emerald-500",
    "bg-rose-500",
    "bg-amber-500",
    "bg-teal-500",
    "bg-fuchsia-500",
    "bg-cyan-500",
  ];
  let hash = 0;
  for (let i = 0; i < seed.length; i++)
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length];
};

export default function View_Calculated_Month_End_Payroll() {
  const [searchParams, setSearchParams] = useSearchParams();
  const API_URL = process.env.REACT_APP_FRONTEND_URL;

  // Default YM
  const today = new Date();
  const defYM = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
    2,
    "0"
  )}`;
  const ym = searchParams.get("ym") || defYM;

  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [page, setPage] = useState(Number(searchParams.get("page") || 1));
  const [limit, setLimit] = useState(Number(searchParams.get("limit") || 10));

  const [year, month] = useMemo(() => {
    const [y, m] = (ym || defYM).split("-");
    return [y, m?.padStart(2, "0")];
  }, [ym, defYM]);

  // Currency
  const [currency, setCurrency] = useState(Cookies.get("currency") || "LKR");
  const [symbol, setSymbol] = useState(Cookies.get("symbol") || "Rs.");
  useEffect(() => {
    setCurrency(Cookies.get("currency") || "LKR");
    setSymbol(Cookies.get("symbol") || "Rs.");
  }, []);

  // Data states
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [dynamicColumns, setDynamicColumns] = useState([]);

  const money = (v) => {
    if (v == null || v === "") return "—";
    const num = Number(v);
    if (!Number.isFinite(num)) return String(v);
    return `${symbol} ${num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Sync URL
  useEffect(() => {
    const sp = new URLSearchParams();
    sp.set("ym", ym);
    if (search) sp.set("search", search);
    sp.set("page", String(page));
    sp.set("limit", String(limit));
    setSearchParams(sp, { replace: true });
  }, [ym, search, page, limit, setSearchParams]);

  // Fetch data
  useEffect(() => {
    if (!year || !month) return;
    const controller = new AbortController();
    (async () => {
      setIsLoading(true);
      setFetchError("");
      try {
        const params = new URLSearchParams({
          year,
          month,
          page,
          limit,
        });
        if (search.trim()) params.set("search", search.trim());

        const res = await fetch(
          `${API_URL}/v1/hris/payroll/genarated-payroll-by-month-and-year?${params.toString()}`,
          {
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            signal: controller.signal,
          }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        const data = Array.isArray(json?.data) ? json.data : [];
        setRows(data);
        setTotalPages(Number(json?.totalPages) || 1);
        setTotalRecords(Number(json?.totalRecords) || data.length || 0);

        if (Number(json?.currentPage) && json.currentPage !== page)
          setPage(json.currentPage);
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error(e);
          setFetchError("Failed to load generated payroll.");
          setRows([]);
        }
      } finally {
        setIsLoading(false);
      }
    })();

    return () => controller.abort();
  }, [API_URL, year, month, search, page, limit]);

  // Dynamic payroll columns
  useEffect(() => {
    fetch(`${API_URL}/v1/hris/payroll/columns`, { credentials: "include" })
      .then((res) => res.json())
      .then((json) => Array.isArray(json) && setDynamicColumns(json))
      .catch((e) => console.error("Columns fetch failed:", e));
  }, [API_URL]);

  const gotoPrev = () => setPage((p) => Math.max(1, p - 1));
  const gotoNext = () => setPage((p) => Math.min(totalPages || 1, p + 1));

  const handleReset = () => {
    setSearch("");
    setPage(1);
    setLimit(10);
    setSearchParams(
      new URLSearchParams({ ym: defYM, page: "1", limit: "10" }),
      { replace: true }
    );
  };

  // ===== CSV EXPORT =====
  const csvEscape = (val) => {
    const s = val == null ? "" : String(val);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const toFixed2 = (v) =>
    v == null || v === ""
      ? ""
      : Number.isFinite(Number(v))
      ? Number(v).toFixed(2)
      : String(v);

  const buildExportColumns = () => [
    { key: "employee_no", label: "Employee No" },
    { key: "employee_fullname", label: "Employee Name" },
    { key: "employee_email", label: "Employee Email" },
    { key: "basic_salary", label: "Basic Salary", fn: toFixed2 },
    { key: "cola", label: "COLA", fn: toFixed2 },
    ...dynamicColumns.map((c) => ({
      key: c.actual_column_name,
      label: c.suggested_name || c.actual_column_name,
      fn: toFixed2,
    })),
    { key: "total_allowances", label: "Total Allowances", fn: toFixed2 },
    { key: "total_deductions", label: "Total Deductions", fn: toFixed2 },
    { key: "net_salary", label: "Net Salary", fn: toFixed2 },
  ];

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams({ year, month });
      if (search.trim()) params.set("search", search.trim());

      const res = await fetch(
        `${API_URL}/v1/hris/payroll/genarated-payroll-by-month-and-year?${params.toString()}`,
        {
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        }
      );

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const allRows = Array.isArray(json?.data) ? json.data : [];

      if (!allRows.length) return toast.warn("No data available to export.");

      const cols = buildExportColumns();
      const headerLine = cols.map((c) => csvEscape(c.label)).join(",");
      const lines = allRows.map((r) =>
        cols
          .map((c) => {
            const raw = c.fn ? c.fn(r[c.key]) : r[c.key] ?? "";
            return csvEscape(raw);
          })
          .join(",")
      );

      const csv = "\uFEFF" + [headerLine, ...lines].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const fileName = `generated-payroll-${year}-${month}.csv`;

      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(a.href);

      toast.success(`Exported ${allRows.length} rows.`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to export CSV.");
    }
  };
  // ===== END CSV EXPORT =====

  return (
    <div className=" font-montserrat">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[25px]">
          Generated Payroll – {ym || "—"}
        </h2>
        <button
          onClick={handleExportCSV}
          className="px-4 py-2 rounded-md bg-[#2495FE] text-white hover:opacity-90"
        >
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-lg p-4 mb-5 shadow-sm flex flex-wrap items-end gap-4">
        <div>
          <label className="text-sm">Month & Year</label>
          <input
            type="month"
            value={ym}
            onChange={(e) => {
              const val = e.target.value;
              if (val) {
                setSearchParams(
                  new URLSearchParams({
                    ym: val,
                    page: "1",
                    limit: String(limit),
                  }),
                  { replace: true }
                );
              }
            }}
            className="border p-2 rounded"
          />
        </div>

        <div>
          <label className="text-sm">Search Employee</label>
          <input
            type="text"
            placeholder="EMP0001 or Name or Email"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="border p-2 rounded w-56"
          />
        </div>

        <div>
          <label className="text-sm">Page size</label>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            className="border p-2 rounded w-24"
          >
            {[10, 20, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={handleReset}
          className="px-4 py-2 border rounded hover:bg-gray-50"
        >
          Reset
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border shadow-md p-2 overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-white text-gray-600 border-b border-t">
            <tr>
              <th className="p-3">Emp No</th>
              <th className="p-3">Employee</th>
              <th className="p-3">Basic</th>
              <th className="p-3">COLA</th>
              {dynamicColumns.map((col) => (
                <th key={col.id} className="p-3">
                  {col.suggested_name || col.actual_column_name}
                </th>
              ))}
              <th className="p-3">Allowances</th>
              <th className="p-3">Deductions</th>
              <th className="p-3">Net</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="999" className="text-center py-6">
                  <div className="inline-block h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </td>
              </tr>
            ) : fetchError ? (
              <tr>
                <td colSpan="999" className="text-center py-6 text-red-600">
                  {fetchError}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan="999" className="text-center py-6">
                  No data found.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="border-b hover:bg-gray-50">
                  <td className="p-3 text-blue-600">{r.employee_no}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs ${avatarBgClass(
                          r.employee_fullname || r.employee_no
                        )}`}
                      >
                        {getInitials(r.employee_fullname || r.employee_no)}
                      </div>
                      <div>
                        <div className="font-semibold">
                          {r.employee_fullname || "—"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {r.employee_email || "—"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3">{money(r.basic_salary)}</td>
                  <td className="p-3">{money(r.cola)}</td>
                  {dynamicColumns.map((col) => (
                    <td key={col.id} className="p-3">
                      {money(r[col.actual_column_name])}
                    </td>
                  ))}
                  <td className="p-3">{money(r.total_allowances)}</td>
                  <td className="p-3">{money(r.total_deductions)}</td>
                  <td className="p-3 font-semibold">{money(r.net_salary)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-3 flex items-center gap-3">
        <button
          onClick={gotoPrev}
          disabled={page <= 1 || isLoading}
          className="border px-3 py-1 rounded disabled:opacity-50"
        >
          Prev
        </button>
        <span className="text-sm">
          Page {page} / {totalPages || 1}
        </span>
        <button
          onClick={gotoNext}
          disabled={page >= (totalPages || 1) || isLoading}
          className="border px-3 py-1 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}
